import React from "react";
import { Box, Button, Grid, Radio, RadioGroup, FormControlLabel, TextField, SxProps } from "@mui/material";

import { OptionType } from "../../types";
import FilterListItem from "../FilterListItem";
import FilterAutocomplete from "../FilterAutocomplete";
import colors from "../../theme/colors";

export interface SearchFilterProps {
  title: string;
  name?: string;
  tooltip?: string;
  placeholder?: string;
  options: OptionType[];
  value: OptionType[] | string;
  icon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  limitTags?: number;
  orValue?: boolean;
  open?: boolean;
  sx?: SxProps;
  setOpen: (value: boolean) => void;
  setOr?: (value: boolean) => void;
  noAutocomplete?: boolean;
  onChange: (value: OptionType[]) => void;
  onTextChange?: (value: string) => void;
}

const SearchFilter: React.FC<SearchFilterProps> = ({
  title = "Search Filter",
  name = "search-filter",
  placeholder = "Search",
  tooltip,
  icon,
  options = [],
  value = [],
  disabled,
  loading,
  limitTags = 8,
  open,
  orValue,
  sx = {},
  setOpen,
  setOr,
  noAutocomplete,
  onChange,
  onTextChange,
}) => {
  const selected = noAutocomplete
    ? typeof value === "string"
      ? value
      : ""
    : Array.isArray(value)
      ? value.length
      : 0;

  return (
    <FilterListItem title={title} tooltip={tooltip} icon={icon} selected={selected} open={open} setOpen={setOpen} sx={sx}>
      {noAutocomplete ? (
        <Box padding={4} paddingTop={0}>
          <TextField
            id="filled-basic"
            label="Search by name"
            variant="outlined"
            size="small"
            name={`${name}-search`}
            style={{ width: "100%", backgroundColor: colors.white }}
            value={typeof value === "string" ? value : ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              onTextChange?.(event.target.value);
            }}
          />
        </Box>
      ) : (
        <FilterAutocomplete
          name={`${name}-search`}
          placeholder={loading ? "Loading..." : placeholder}
          value={Array.isArray(value) ? value : []}
          limitTags={limitTags}
          options={options}
          onChange={onChange}
          disabled={disabled || loading}
        />
      )}

      {setOr && (
        <Box padding={4} paddingTop={0}>
          <RadioGroup
            name="control"
            value={orValue ? 1 : 0}
            onChange={(_, value) => {
              setOr(!!parseInt(value));
            }}
          >
            <FormControlLabel value={0} disabled={disabled} control={<Radio color="primary" />} label="Control AND" />
            <FormControlLabel value={1} disabled={disabled} control={<Radio color="primary" />} label="Control OR" />
          </RadioGroup>
        </Box>
      )}
      {!noAutocomplete && (
        <Grid container spacing={2}>
          <Grid>
            <Button
              id={`${name}-clear`}
              variant="outlined"
              size="small"
              disabled={disabled || loading || !Array.isArray(value) || value.length === 0}
              onClick={() => {
                onChange([]);
              }}
              style={{ backgroundColor: colors.white }}
            >
              Clear
            </Button>
          </Grid>

          <Grid>
            <Button
              id={`${name}-select-all`}
              variant="outlined"
              size="small"
              disabled={disabled || loading || !Array.isArray(value) || value.length === options.length}
              onClick={() => {
                onChange(options);
              }}
              style={{ backgroundColor: colors.white }}
            >
              Select all
            </Button>
          </Grid>
        </Grid>
      )}
    </FilterListItem>
  );
};

export default SearchFilter;
